import { useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export const BrandCard = ({ canEdit }) => {
  const [f, setF] = useState({ color: "#1E40AF", nombre_visible: "" });
  const [logo, setLogo] = useState(null);

  const loadLogo = () =>
    api.get("/company/marca/logo", { responseType: "blob" }).then((r) => setLogo(URL.createObjectURL(r.data))).catch(() => setLogo(null));

  useEffect(() => {
    api.get("/company").then((r) => setF({ color: r.data.marca.color, nombre_visible: r.data.marca.nombre_visible || r.data.nombre_fantasia, whatsapp: r.data.marca.whatsapp || "", app_url: r.data.marca.app_url || "", color_boton: r.data.marca.color_boton || "", color_boton2: r.data.marca.color_boton2 || "" }));
    loadLogo();
  }, []);

  const save = async () => {
    try {
      await api.put("/company/marca", { ...f, whatsapp: f.whatsapp?.trim() || null, app_url: f.app_url?.trim() || null, color_boton: f.color_boton || null, color_boton2: f.color_boton2 || null });
      toast.success("Marca actualizada");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) return toast.error("El logo debe ser PNG o JPG");
    if (file.size > 1_000_000) return toast.error("El logo no puede superar 1 MB");
    const fd = new FormData();
    fd.append("archivo", file);
    try {
      await api.post("/company/marca/logo", fd);
      toast.success("Logo actualizado");
      loadLogo();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="brand-card">
      <div>
        <p className="font-heading font-semibold">Marca del link KYC</p>
        <p className="text-xs text-slate-500">Tus clientes verán este logo, nombre y color al verificarse.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Logo (PNG o JPG, máx. 1 MB)</Label>
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-lg border bg-slate-50 grid place-items-center overflow-hidden">
              {logo ? <img src={logo} alt="Logo" className="h-full w-full object-contain" data-testid="brand-logo-preview" /> : <span className="text-[10px] text-slate-400">Sin logo</span>}
            </div>
            {canEdit && (
              <label className="inline-flex items-center gap-2 text-sm border rounded-md px-3 h-9 cursor-pointer hover:bg-slate-50">
                <Upload size={14} /> Subir
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={upload} data-testid="brand-logo-input" />
              </label>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Nombre comercial para mostrar</Label>
          <Input value={f.nombre_visible} disabled={!canEdit} maxLength={60} onChange={(e) => setF({ ...f, nombre_visible: e.target.value })} data-testid="brand-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">WhatsApp para agendar videollamadas</Label>
          <Input value={f.whatsapp || ""} disabled={!canEdit} maxLength={20} placeholder="+56 9 1234 5678" onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="brand-whatsapp-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Link de tu app (botón "Volver" al terminar)</Label>
          <Input value={f.app_url || ""} disabled={!canEdit} maxLength={300} placeholder="https://" onChange={(e) => setF({ ...f, app_url: e.target.value })} data-testid="brand-app-url-input" />
        </div>
        <div className="flex flex-wrap items-end gap-4" data-testid="brand-boton">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Color del botón</Label>
            <input type="color" value={f.color_boton || f.color} disabled={!canEdit} onChange={(e) => setF({ ...f, color_boton: e.target.value })} className="h-10 w-16 cursor-pointer block" data-testid="brand-boton-color-input" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled={!canEdit} checked={!!f.color_boton2} onChange={(e) => setF({ ...f, color_boton2: e.target.checked ? "#E0A526" : "" })} data-testid="brand-degradado-toggle" /> Degradado
          </label>
          {f.color_boton2 && <input type="color" value={f.color_boton2} disabled={!canEdit} onChange={(e) => setF({ ...f, color_boton2: e.target.value })} className="h-10 w-16 cursor-pointer block" data-testid="brand-boton-color2-input" />}
          <span className="h-10 px-5 rounded-xl text-white text-sm font-semibold grid place-items-center" data-testid="brand-boton-preview"
            style={{ background: f.color_boton2 ? `linear-gradient(135deg, ${f.color_boton || f.color}, ${f.color_boton2})` : (f.color_boton || f.color) }}>Botón</span>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Color principal</Label>
            <input type="color" value={f.color} disabled={!canEdit} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-10 w-16 cursor-pointer block" data-testid="brand-color-input" />
          </div>
          {canEdit && <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="brand-save-button">Guardar</Button>}
        </div>
      </div>
    </div>
  );
};
